// Exception work: the work done when items leave the main flow of a process and return to it later, such as
// collecting information an input is missing before it can be processed. Where routes split and join again at a
// step that sums its inputs, the route most items take is the main flow and the others are detours. The actions on a
// detour are exception work, unless the user has said otherwise on the action (normal work, such as an approval
// that larger orders need), and the user can mark any action as exception work. Exception work is friction: the
// EPM scorecard counts its cost beside the dead ends.
//
// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder.
import { ACTION_FREQUENCY_TYPE, EDGE_TYPE, NODE_SUPERTYPE, NODE_TYPE, WORK_TYPE } from "./linqset-base"
import { getDurationHours, getInsights, Linqset, LinqsetEdge, LinqsetNode } from "./linqset-model"

// the kind of work an action is: what the user said ("normal" being value adding, as saved before there were four
// kinds), or as LINQ reads it: waiting where nobody does the step, exception work on a detour and value adding otherwise
export const workKindOf = (said:WORK_TYPE|null|undefined, onDetour:boolean|undefined, waits = false):WORK_TYPE =>
    said === WORK_TYPE.NORMAL ? WORK_TYPE.VALUE_ADDING : said || (waits ? WORK_TYPE.WAITING : onDetour ? WORK_TYPE.EXCEPTION : WORK_TYPE.VALUE_ADDING)

// a step that takes time but that no role or system performs: items wait there, for someone or something outside it.
// Only where other steps have roles or systems: a model with none on any step hasn't had its roles added yet
const performs = (e:LinqsetEdge) => e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.AUTOMATE
export const readAsWait = (node:LinqsetNode, edges:LinqsetEdge[]) =>
    node.superType === NODE_SUPERTYPE.ACTION && node.type !== NODE_TYPE.CAPTURE && node.type !== NODE_TYPE.OUTCOME &&
    getDurationHours(node.properties?.duration) > 0 &&
    !edges.some(e => e.targetKey === node._key && performs(e)) && edges.some(performs)

// whether items wait at a step, as the user said or as LINQ reads it. Known before the work kinds are (they wait for
// the detours), so the hours worked out for the step can leave the wait out
export const isWait = (node:LinqsetNode, edges:LinqsetEdge[]) =>
    workKindOf(node.properties?.workType, false, readAsWait(node, edges)) === WORK_TYPE.WAITING

// enough to compare the alternatives without enumerating every route through a large, heavily branched process
export const MAX_DETOUR_ROUTES = 50

// the edges items flow along within a process
const FLOW_EDGES = [EDGE_TYPE.CREATE, EDGE_TYPE.SUPPORT, EDGE_TYPE.JOINS]

// every route back from a node to where the process starts, each beginning with the node. A node already on a route
// isn't visited again, so a loop in the process ends the route
export const findRoutesBack = <T>(start:T, previous:(node:T) => T[], maxRoutes = MAX_DETOUR_ROUTES):T[][] => {
    const routes:T[][] = []
    const walk = (route:T[]) => {
        if (routes.length >= maxRoutes) return
        const next = previous(route[route.length - 1]).filter(p => !route.includes(p))
        if (next.length === 0) {
            routes.push(route)
            return
        }
        next.forEach(p => walk([...route, p]))
    }
    walk([start])
    return routes
}

export interface DetourRoute<T> {
    nodes:T[]           // the nodes on the detour between the split and the join, nearest the join first, that aren't on the main route
    items:number        // items a year taking it
    share:number        // of the items leaving the split
}

export interface Detours<T> {
    split:T
    join:T
    main:T[]            // the nodes on the main route between the split and the join
    detours:DetourRoute<T>[]
}

/**
 * For a step where routes join: finds the nearest node all its routes back pass through (the split), takes the route
 * most items leave the split along as the main flow, and the rest as detours. Undefined if the routes never meet, or
 * there is only one. itemsAlong gives the items a year flowing from a node to the next one on a route
 */
export const findDetours = <T>(routes:T[][], itemsAlong:(from:T, to:T) => number):Detours<T>|undefined => {
    if (routes.length < 2) return undefined
    const [first, ...others] = routes
    const splitIndex = first.findIndex((node, i) => i > 0 && others.every(r => r.includes(node)))
    if (splitIndex < 0) return undefined
    const split = first[splitIndex]
    const join = first[0]

    // each route from the split to the join: the nodes between them, and the items leaving the split along it
    const ways = routes.map(r => {
        const at = r.indexOf(split)
        return { nodes: r.slice(1, at), items: Math.max(0, itemsAlong(split, r[at - 1])) }
    })
    // the same way can be reached more than once where routes differ only further back
    const distinct = new Map<string, typeof ways[number]>()
    ways.forEach(w => {
        const key = w.nodes.map(n => String((n as any)._key ?? n)).join('>')
        if (!distinct.has(key)) distinct.set(key, w)
    })
    const unique = [...distinct.values()]
    const total = unique.reduce((sum, w) => sum + w.items, 0)
    if (total <= 0) return undefined
    const main = unique.reduce((best, w) => w.items > best.items ? w : best)
    const detours = unique.filter(w => w !== main && w.items > 0).map(w => ({
        nodes: w.nodes.filter(n => !main.nodes.includes(n)),
        items: w.items,
        share: w.items/total,
    })).filter(d => d.nodes.length > 0)
    return { split, join, main: main.nodes, detours }
}

/**
 * Reads the detours in a model and sets each node's part in them, then which actions are exception work: those the
 * user marked, or those on a detour the user hasn't marked as normal work. Run once the items through each node are
 * known (the time pass), before the model's totals are made
 */
export const setExceptionWork = (linqset:Linqset) => {
    const nodes = linqset.nodes.filter(n => (n.superType === NODE_SUPERTYPE.ACTION || n.superType === NODE_SUPERTYPE.INFORMATION) && (linqset.isFilterModel() || n.modelKey === linqset.modelKey))
    const keys = new Set(nodes.map(n => n._key))
    const byKey = new Map(nodes.map(n => [n._key, n]))
    const edges = linqset.edges.filter(e => FLOW_EDGES.includes(e.type) && keys.has(e.sourceKey) && keys.has(e.targetKey) && e.sourceKey !== e.targetKey)
    const incoming = new Map<string, LinqsetEdge[]>()
    edges.forEach(e => (incoming.get(e.targetKey) || incoming.set(e.targetKey, []).get(e.targetKey)!).push(e))
    const between = new Map(edges.map(e => [e.sourceKey + '>' + e.targetKey, e]))

    nodes.forEach(n => {
        const i = getInsights(n, linqset)
        i.onDetour = false
        i.detourSplitKey = undefined
        i.detourJoinKey = undefined
        i.detourShare = 0
        i.exceptionWork = n.superType === NODE_SUPERTYPE.ACTION && n.properties?.workType === WORK_TYPE.EXCEPTION
        i.workKind = n.superType === NODE_SUPERTYPE.ACTION ? workKindOf(n.properties?.workType, false, readAsWait(n, linqset.edges)) : undefined
    })

    const previous = (n:LinqsetNode) => [...new Set((incoming.get(n._key) || []).map(e => byKey.get(e.sourceKey)).filter((p):p is LinqsetNode => !!p))]
    // the items a year leaving a node along its connection to the next: its share of what the node processes
    const itemsAlong = (from:LinqsetNode, to:LinqsetNode) => {
        const e = between.get(from._key + '>' + to._key)
        const items = getInsights(from, linqset).numberProcessed || 0
        return e && e.outputPercentage !== undefined && e.outputPercentage !== null ? items*e.outputPercentage/100 : items
    }

    // where routes join at a step that sums its inputs
    nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && previous(n).length > 1 && (n.properties?.actionProcessType || ACTION_FREQUENCY_TYPE.SUM) === ACTION_FREQUENCY_TYPE.SUM)
        .forEach(join => {
            const found = findDetours(findRoutesBack(join, previous), itemsAlong)
            if (!found) return
            found.detours.forEach(d => d.nodes.forEach(n => {
                const i = getInsights(n, linqset)
                // a node on more than one detour keeps the one most items take
                if (i.onDetour && i.detourShare >= d.share) return
                i.onDetour = true
                i.detourSplitKey = found.split._key
                i.detourJoinKey = found.join._key
                i.detourShare = d.share
            }))
        })

    nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).forEach(n => {
        const i = getInsights(n, linqset)
        i.workKind = workKindOf(n.properties?.workType, i.onDetour, readAsWait(n, linqset.edges))
        i.exceptionWork = i.workKind === WORK_TYPE.EXCEPTION
    })
}
